import React from 'react';

import DeleteExternalUserDialog from '@/components/organism/ExternalUsers/DeleteExternalUserDialog';
import ExternalUsersControls from '@/components/organism/ExternalUsers/ExternalUsersControls';
import ExternalUsersTable from '@/components/organism/ExternalUsers/ExternalUsersTable';
import type {
  ExternalUser,
  ExternalUserSortDir,
  ExternalUserSortKey,
  ExternalUserStatusFilter,
} from '@/types/external-users.types';

interface ExternalUsersPageViewProps {
  visibleUsers: ExternalUser[];
  error: string | null;
  searchValue: string;
  sortKey: ExternalUserSortKey;
  sortDir: ExternalUserSortDir;
  deptOrgFilter: string;
  deptOrgOptions: string[];
  statusFilter: ExternalUserStatusFilter;
  pendingDelete: ExternalUser | null;
  showEmptyState: boolean;
  showEmptySearchState: boolean;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  onSortChange: (key: ExternalUserSortKey) => void;
  onDeptOrgFilterChange: (value: string) => void;
  onStatusFilterChange: (value: ExternalUserStatusFilter) => void;
  onDeleteRequest: (user: ExternalUser) => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
  onRetry: () => void;
}

function ExternalUsersPageView({
  visibleUsers,
  error,
  searchValue,
  sortKey,
  sortDir,
  deptOrgFilter,
  deptOrgOptions,
  statusFilter,
  pendingDelete,
  showEmptyState,
  showEmptySearchState,
  onSearchChange,
  onSearchClear,
  onSortChange,
  onDeptOrgFilterChange,
  onStatusFilterChange,
  onDeleteRequest,
  onDeleteConfirm,
  onDeleteCancel,
  onRetry,
}: ExternalUsersPageViewProps): React.ReactElement {
  return (
    <div className="flex-1 p-6">
      <div className="space-y-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold text-foreground">External users</h1>
        </div>

        {error !== null && (
          <div
            className="flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm text-destructive"
            data-testid="external-users-error"
          >
            <span>{error}</span>
            <button
              type="button"
              className="font-medium underline underline-offset-2"
              onClick={onRetry}
            >
              Retry
            </button>
          </div>
        )}

        <ExternalUsersControls
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          onSearchClear={onSearchClear}
          deptOrgFilter={deptOrgFilter}
          deptOrgOptions={deptOrgOptions}
          onDeptOrgFilterChange={onDeptOrgFilterChange}
          statusFilter={statusFilter}
          onStatusFilterChange={onStatusFilterChange}
        />

        <ExternalUsersTable
          users={visibleUsers}
          sortKey={sortKey}
          sortDir={sortDir}
          onSortChange={onSortChange}
          onDeleteRequest={onDeleteRequest}
          showEmptyState={showEmptyState}
          showEmptySearchState={showEmptySearchState}
        />

        <DeleteExternalUserDialog
          user={pendingDelete}
          onConfirm={onDeleteConfirm}
          onCancel={onDeleteCancel}
        />
      </div>
    </div>
  );
}

export default ExternalUsersPageView;
